/**
 * CompanyTradeSelect — Dropdown groupé des métiers BTP
 *
 * Alimenté par useTradeReferences() (GET /api/core/trades).
 * Quand l'utilisateur change de métier, le businessType associé est aussi renvoyé via onChange.
 *
 * Utilisé dans : CompanySettingsPage, Step1CompanyInfo (onboarding)
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTradeReferences } from '@/hooks/useTradeReferences';

interface CompanyTradeSelectProps {
  value: string;
  onChange: (tradeCode: string, businessType?: string) => void;
  /** Classes CSS du <select> — permet d'adapter au contexte (settings vs onboarding) */
  selectClasses: string;
  /** Label personnalisé (par défaut : t('settings.company.companyTrade')) */
  label?: string;
  /** Placeholder personnalisé */
  placeholder?: string;
  /** Masquer le label (ex: quand le parent gère déjà le label) */
  hideLabel?: boolean;
}

export const CompanyTradeSelect: React.FC<CompanyTradeSelectProps> = ({
  value,
  onChange,
  selectClasses,
  label,
  placeholder,
  hideLabel = false,
}) => {
  const { t } = useTranslation();
  const { categories, isLoading } = useTradeReferences();

  const handleChange = (tradeCode: string) => {
    if (!tradeCode) {
      onChange('');
      return;
    }
    // Find the trade to get its associated businessType
    const trade = categories
      .flatMap((cat) => cat.trades)
      .find((tr) => tr.code === tradeCode);
    onChange(tradeCode, trade?.businessType);
  };

  return (
    <div>
      {!hideLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label || t('settings.company.companyTrade')}
        </label>
      )}
      <select
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        className={selectClasses}
        disabled={isLoading}
      >
        <option value="">{placeholder || t('settings.company.companyTradePlaceholder')}</option>
        {categories.map((cat) => (
          <optgroup key={cat.category} label={cat.category}>
            {cat.trades.map((trade) => (
              <option key={trade.code} value={trade.code}>
                {trade.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
};
